import { createUseStyles } from 'react-jss'
import { THEME } from 'components/theme'

export const AccountReportStyles = createUseStyles({
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: THEME.brandWhite,
    overflow: 'hidden'
  },
  rowAccountName: {
    fontWeight: 600,
    color: THEME.brandBlack
  },
  // The table's scroll area, with a wider, always-visible scrollbar (the default
  // overlay scrollbar is too thin to grab on a long report).
  scrollArea: {
    flex: 1,
    overflowY: 'scroll',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#b6c0c3 transparent',
    '&::-webkit-scrollbar': {
      width: 14,
      height: 14
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#b6c0c3',
      borderRadius: 8,
      border: `3px solid ${THEME.brandWhite}`,
      backgroundClip: 'content-box'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#98a4a7',
      backgroundClip: 'content-box'
    }
  },
  // Zebra striping + hover highlight so a wide row is easy to read across to Balance.
  ledgerRow: {
    transition: 'background-color 120ms ease',
    '&:nth-child(even)': {
      backgroundColor: THEME.brandWhite
    },
    '&:hover': {
      backgroundColor: THEME.lightGrey
    }
  },
  // Jump-to-top control, shown once the list is scrolled.
  toTop: {
    position: 'absolute',
    right: 24,
    bottom: 24,
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: THEME.brandGreen,
    color: THEME.onBrand,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(28, 135, 59, 0.4)',
    zIndex: 20,
    transition: 'background 0.15s ease, transform 0.15s ease',
    '&:hover': {
      background: THEME.brandTintGreen,
      transform: 'translateY(-1px)'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${THEME.brandLightGrey}`
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: THEME.brandBlack
  },
  subtitle: {
    fontSize: 13,
    color: THEME.infoGrey || '#757575'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  actionBtn: {
    border: `1px solid ${THEME.brandGreen}`,
    background: 'transparent',
    color: THEME.brandGreen,
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  closeBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    color: THEME.brandBlack
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 20px',
    flexWrap: 'wrap'
  },
  tabs: {
    display: 'inline-flex',
    gap: 4,
    background: THEME.lightGrey,
    borderRadius: 8,
    padding: 4,
    marginBottom: 12,
    width: 'fit-content'
  },
  tab: {
    border: 'none',
    background: 'transparent',
    padding: '6px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    color: THEME.infoGrey || '#757575',
    cursor: 'pointer'
  },
  tabActive: {
    background: THEME.brandWhite,
    color: THEME.brandBlack,
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
  },
  totals: {
    display: 'flex',
    gap: 24,
    padding: '8px 20px 16px'
  },
  totalItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  totalLabel: {
    fontSize: 12,
    color: THEME.infoGrey || '#757575'
  },
  totalValue: {
    fontSize: 16,
    fontWeight: 600,
    color: THEME.brandBlack
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: '0 20px 20px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13
  },
  th: {
    position: 'sticky',
    top: 0,
    background: THEME.brandBGLight,
    textAlign: 'left',
    padding: '10px 12px',
    fontWeight: 600,
    color: THEME.brandBlack,
    borderBottom: `1px solid ${THEME.brandLightGrey}`
  },
  td: {
    padding: '10px 12px',
    borderBottom: `1px solid ${THEME.brandBGMedium}`,
    color: THEME.brandBlack
  },
  right: {
    textAlign: 'right'
  },
  due: {
    color: '#D32F2F',
    fontWeight: 600
  },
  advance: {
    color: '#388E3C',
    fontWeight: 600
  },
  center: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 40,
    textAlign: 'center'
  },
  progressBarTrack: {
    width: 280,
    height: 8,
    borderRadius: 4,
    background: THEME.brandLightGrey,
    overflow: 'hidden'
  },
  progressBarFill: {
    height: '100%',
    background: THEME.brandGreen,
    transition: 'width 0.2s ease'
  },
  // Skeleton loading state: placeholder rows shaped like the real report table so the
  // report feels like it's rendering in. The header subtitle keeps the live sync count.
  '@keyframes accReportShimmer': {
    from: { backgroundPosition: '-468px 0' },
    to: { backgroundPosition: '468px 0' }
  },
  skeletonArea: {
    flex: 1,
    overflow: 'hidden'
  },
  skeletonRow: {
    // Subtle top-down cascade so rows don't all pulse in perfect lockstep.
    '&:nth-child(even) $skBar': {
      opacity: 0.9
    }
  },
  skBar: {
    display: 'inline-block',
    height: 12,
    maxWidth: '100%',
    borderRadius: 6,
    background: 'linear-gradient(90deg, #edf0f1 25%, #f6f8f9 50%, #edf0f1 75%)',
    backgroundSize: '936px 100%',
    animationName: '$accReportShimmer',
    animationDuration: '1.3s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear'
  },
  '@media (prefers-reduced-motion: reduce)': {
    skBar: {
      animation: 'none',
      background: THEME.lightGrey
    }
  },
  muted: {
    color: THEME.infoGrey || '#757575',
    fontSize: 13
  },
  emptyName: {
    fontStyle: 'italic',
    color: THEME.infoGrey || '#757575'
  },
  deletedTag: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '1px 6px',
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '16px',
    color: '#D32F2F',
    background: THEME.brandLightRed,
    borderRadius: 4,
    verticalAlign: 'middle'
  },
  discountLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    marginTop: 8,
    borderTop: `1px solid ${THEME.brandLightGrey}`,
    fontSize: 14,
    color: THEME.brandBlack
  }
})
